import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import { CardOrientation } from '@space-core/components/customCard/base/enum'
import type { BundleCard } from '@space-core/components/customCard/bundle/types'
import ProductCard from '@space-core/components/customCard/product/desktop'
import ProductCardMobile from '@space-core/components/customCard/product/mobile'
import { ImageOrientation } from '@space-core/components/customImage/enum'
import Grid from '@space-core/components/grid'
import { GridDisplay } from '@space-core/components/grid/enum'
import Icon from '@space-core/components/icon'
import Modal from '@space-core/components/modal'
import { VariationModal } from '@space-core/components/modal/enum'
import ModalBody from '@space-core/components/modalBody'
import ModalHeader from '@space-core/components/modalHeader'
import Tag from '@space-core/components/tag'
import { useMediaQuery } from '@space-core/hooks'
import useTranslation from '@space-core/hooks/useTranslation'
import { ScreenSizes, focusVisible } from '@space-core/styles'
import type { Product } from '@space-shell/common/services/bundle'
import { ProductUtils } from '@space-shell/common/utils/product'
import styled from 'styled-components'

import Description from './description'
import Footer from './footer'

const HotModalBody = styled(ModalBody)`
  overflow: auto;
  height: 60vh;
`

const CloseButton = styled.button`
  background: 0;
  ${focusVisible}
`

export type ModalBundleProps = {
  show: boolean
  onClose: () => void
  products: Product[]
  bundle: BundleCard
}

const ModalBundle = ({ show = false, onClose, products, bundle }: ModalBundleProps) => {
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.lg})`)
  const { t } = useTranslation()

  return (
    <Modal className='_mb-12' show={show} onClose={onClose} variation={VariationModal.BUNDLE}>
      <ModalHeader className='_text-4 _px-7 _px-md-5 _pt-7 _pt-md-5'>
        <div className='_d-flex _justify-content-between _align-items-center'>
          <p className='_m-0 _text-gray-900'>
            <span className='_mr-2'>{t('bundle.modal_title')}</span>
            <strong>{bundle.name}</strong>
            <Tag className='_ml-2 _mb-2' variation='green' data-test='card-tag' role='term'>
              {bundle.subscription ? t('bundle.subscription_payment') : t('bundle.unic_payment')}
            </Tag>
          </p>
          <CloseButton
            onClick={onClose}
            data-test='button-modal-close'
            className='_align-items-center _border-0 _text-5 _text-gray-900 _line-height-sm _cursor-pointer'>
            <Icon icon={faXmark} />
          </CloseButton>
        </div>
      </ModalHeader>
      <HotModalBody className='_pt-0 _overflow-y-auto _px-7 _px-md-5'>
        <Description isMobile={isMobile} description={bundle.description} />
        <Grid
          cardOrientation={CardOrientation.HORIZONTAL}
          type={isMobile ? GridDisplay.MOBILE : GridDisplay.DESKTOP}
          className='_mt-5'>
          {products.map((product) => {
            return isMobile ? (
              <ProductCardMobile
                key={product.id}
                product={ProductUtils.bundleMapping(product, t)}
                items={products.length}
                orientation={CardOrientation.HORIZONTAL}
                imageOrientation={ImageOrientation.HORIZONTAL}
              />
            ) : (
              <ProductCard
                key={product.id}
                product={ProductUtils.bundleMapping(product, t)}
                items={products.length}
                orientation={CardOrientation.HORIZONTAL}
                imageOrientation={ImageOrientation.HORIZONTAL}
              />
            )
          })}
        </Grid>
      </HotModalBody>
      <Footer
        isMobile={isMobile}
        id={String(bundle.id)}
        name={bundle.name}
        price={bundle.price}
        link={bundle.hotlink}
      />
    </Modal>
  )
}

export default ModalBundle
